
























import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import creditnotesService from '@/modules/creditnotes/creditnotesService';
import notificationService from '@/services/notificationService';
import cCard from '@/components/default/Card.vue';

import Datagrid from '@/components/default/Datagrid.vue';
import dxCustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';

import { CreditNotesClient, CreateCreditNoteCommand, CreditNotesLookupQuery, DataSourceLoadOptionsBase, SendCreditNotesToAccountantCommand } from '@/data/web-api-client';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import dxDataGrid from 'devextreme/ui/data_grid';

@Component({
  components: {
    Datagrid,
    cCard,
    DxLoadPanel,
  },
})
export default class CreditNotesTable extends Vue {
  @Prop({ required: false })
  private contactId: string | undefined;

  @Prop({ required: false })
  private projectId: string | undefined;

  @Prop({ required: false, default: true })
  standalone?: boolean;

  @Prop({ required: false, default: true })
  expanded?: boolean;

  @Prop({ required: false })
  enableSelection?: boolean;

  @Prop({ required: false, default: false })
  public disabled!: boolean;

  loading = false;
  adding = false;

  datasource: DataSource = new DataSource({
    store: new dxCustomStore({
      key: 'id',
      load: async (loadOptions) => {
        try {
          const options = new DataSourceLoadOptionsBase(
            Object.assign({}, loadOptions, {
              stringToLower: true,
            })
          );
          options.init(Object.assign({}, loadOptions, { stringToLower: true }));
          const result = await new CreditNotesClient(undefined, this.$axios).lookup(
            new CreditNotesLookupQuery({
              options,
              contactId: this.contactId,
              projectId: this.projectId,
            })
          );

          return {
            data: result.data,
            totalCount: result.totalCount,
          };
        } catch (err) {
          return {
            data: [],
            totalCount: 0,
          };
        }
      },
      byKey: async (val: any) => {
        return (await creditnotesService.getSingle(val)).data;
      },
      remove: async () => { }
    }),
    paginate: true,
  });

  private client = new CreditNotesClient(undefined, this.$axios);

  openDetails(e: any) {
    if (e.row && !e.row.isEditing && e.column && e.column.type !== 'buttons' && e.column.type !== 'selection') {
      this.$router.push({ path: `/creditnotes/${e.row.data.id}`, query: { checkProjectOnHold: this.projectId ? 'false' : 'true' } });
    }
  }

  async addRow() {
    try {
      this.adding = true;
      const command = new CreateCreditNoteCommand({
        projectId: this.projectId,
      });

      const res = await this.client.create(command);

      this.$router.push({ path: `/creditnotes/${res}` });
    } catch {
      notificationService.showErrorMessage('Er is iets verkeerdgelopen tijdens het aanmaken van de creditnota');
    } finally {
      this.adding = false;
    }
  }

  deleteRow(e: any) {
    const self = this;
    e.cancel = new Promise(async (resolve, reject) => {
      try {
        await creditnotesService.delete((e.data as ICreditNoteListItemDto).id);
        notificationService.saved();
        resolve(false);
      } catch (err) {
        if (err?.response?.status === 403) {
          notificationService.showAxiosError(err);
          resolve(true);

          return;
        }

        notificationService.failedToSave();
        resolve(true);
      }
    });
  }

  async sendToAccountant() {
    const creditnoteIds = this.gridInstance?.getSelectedRowsData()?.map((i) => i.id) ?? [];

    if (creditnoteIds.length === 0) {
      return;
    }

    const command = new SendCreditNotesToAccountantCommand({
      creditNoteIds: [...creditnoteIds],
    });

    try {
      await this.client.sendToAccountant(command);

      notificationService.showNotification("De creditnota's worden in de achtergrond verstuurd naar de boekhouding", 'info');
    } catch (err) {
      notificationService.showAxiosError(err);
    }
  }

  get columns() {
    return [
      {
        dataField: 'documentNumber',
        caption: this.$t('objects.invoices.DocumentNumber'),
      },
      {
        dataField: 'projectNameNormalized',
        caption: 'Projectnaam',
        calculateCellValue(value: any) {
          if (value && value.projectName) {
            return value.projectName;
          }
          return '';
        },
      },
      {
        dataField: 'customer.displayName',
        caption: this.$t('objects.quotations.fields.Customer'),
      },
      {
        dataField: 'customerContact.displayName',
        caption: this.$t('objects.quotations.fields.CustomerContact'),
      },
      {
        dataField: 'date',
        caption: this.$t('objects.invoices.Date'),
        dataType: 'date',
        format: 'dd/MM/yyyy',
      },
      {
        dataField: 'status',
        caption: this.$t('objects.invoices.Status'),
        lookup: { dataSource: creditnotesService.getStatusOptions(), displayExpr: 'text', valueExpr: 'value' },
      },
      {
        dataField: 'total',
        caption: this.$t('objects.invoices.Total'),
        format: {
          type: 'currency',
          precision: 2,
        },
        alignment: 'left',
      },
      {
        dataField: 'toPay',
        caption: 'Nog te betalen',
        format: {
          type: 'currency',
          precision: 2,
        },
        alignment: 'left',
      },
      {
        dataField: 'sentToAccountantDate',
        caption: 'Boekhouding',
        dataType: 'date',
        format: 'dd/MM/yyyy',
      },
      {
        type: 'buttons',
        buttons: [
          {
            hint: 'Project openen',
            icon: 'fas fa-briefcase',
            visible(e: any) {
              return !e.row.isEditing && e.row.data.projectId;
            },
            onClick: (e: any) => {
              this.$router.push({ path: `/projects/${e.row.data.projectId}` });
            },
          },
          'delete',
        ],
      },
    ];
  }

  get toolbarOptions() {
    const self = this;
    return [
      {
        location: 'before',
        widget: 'dxButton',
        visible: !this.disabled,
        options: {
          icon: self.adding ? 'fas fa-spin fa-spinner' : 'fas fa-plus',
          text: 'Toevoegen',
          stylingMode: 'outlined',
          type: 'success',
          disabled: self.adding,
          onClick: () => {
            self.addRow();
          },
        },
      },
    ];
  }

  get gridInstance() {
    return (this.$refs?.grid as any)?.$refs.creditnotesTable?.instance as dxDataGrid;
  }
}
